/**
 * Grid Layout Strategy
 *
 * Grid mode displays two dimensions as a 2D grid (rows and columns),
 * with the third dimension controlled via a slider in the header/footer.
 *
 * This file contains both the data transformation logic and control
 * placement logic for grid layouts.
 */

import * as _ from 'lodash';

import {MediaBrowserPanelConfig} from '../common';
import {LayoutStrategy} from './LayoutStrategy';
import {GridData, SlotRegistry, TransformInput} from './types';
import {getAxisValuesWithOptions} from './utils/axisValues';
import {buildGridRows, getControlledDimension} from './utils/gridRows';
import {
  caption,
  indexSlider,
  label,
  stepSlider,
  xAxisRangeSlider,
} from './utils/slots';

// Debug logging flag - set to false to disable verbose console logs
const DEBUG = false;
const log = DEBUG ? console.log : () => {};
const warn = DEBUG ? console.warn : () => {};

export const GridLayout: LayoutStrategy = {
  id: 'grid',

  transform(input: TransformInput): GridData {
    const {runs, stepMeta, currentStep, currentIndex, config, allMasks} = input;
    const {gridSettings, mediaKeys, maxYAxisCount = 100} = config;
    const xAxis = gridSettings?.xAxis ?? 'step';
    const yAxis = gridSettings?.yAxis ?? 'run';
    const snapToExistingStep = config.snapToExistingStep ?? true;

    if (!mediaKeys || mediaKeys.length === 0) {
      return {rows: [], metadata: {layoutType: 'grid', totalCells: 0}};
    }

    if (runs.length === 0) {
      return {rows: [], metadata: {layoutType: 'grid', totalCells: 0}};
    }

    log('[GridLayout] Starting transform:', {
      xAxis,
      yAxis,
      snapToExistingStep,
      runCount: runs.length,
      maxRows: maxYAxisCount,
    });

    // Use column-by-column logic for all grid modes
    // The old makeLayout uses a single nested loop structure that works for all cases
    return buildColumnByColumnLogic(
      input,
      xAxis,
      yAxis,
      snapToExistingStep,
      maxYAxisCount
    );
  },

  buildSlotRegistry(config: MediaBrowserPanelConfig): SlotRegistry {
    const xAxis = config.gridSettings?.xAxis ?? 'step';
    const yAxis = config.gridSettings?.yAxis ?? 'run';
    const columnCount = config.columnCount ?? 1;
    const maxYAxisCount = config.maxYAxisCount ?? 1;

    // Build slot registry organized by LOCATION
    const slots: SlotRegistry = {
      'content-header': [],
      'column-header': [],
      'row-header': [],
      'row-footer': [],
      'tile-header': [],
      'tile-footer': [],
      'column-footer': [],
      'content-footer': [],
    };

    // Add controls based on which dimensions are on axes vs controlled

    // Step control
    if (xAxis === 'step') {
      // Step is on x-axis (column headers)
      slots['column-header']!.push(label('step'));
    } else if (yAxis === 'step') {
      // Step is on y-axis (row headers)
      slots['row-header']!.push(label('step'));
    } else {
      // Step is controlled (slider in header)
      slots['content-footer']!.push(stepSlider());
    }

    // Index control
    if (xAxis === 'index') {
      // Index is on x-axis (column headers)
      slots['column-footer']!.push(label('index'));
    } else if (yAxis === 'index') {
      // Index is on y-axis (row headers)
      // matching prod with this, but it seems inconsistent with the other axes
      slots['row-header']!.push(label('index'));
    } else {
      // Index is controlled (slider in footer)
      slots['content-footer']!.push(indexSlider());
    }

    // Run control
    if (xAxis === 'run') {
      // Run is on x-axis (show on each tile)
      slots['tile-header']!.push(label('run'));
    } else if (yAxis === 'run') {
      // Run is on y-axis (row headers)
      slots['row-header']!.push(label('run'));
    }

    // Add x-axis range slider when xAxis is step and we have multiple columns
    // (allows zooming into a subset of the horizontal axis)
    if (xAxis === 'step' && columnCount > 1) {
      slots['content-footer']!.push(xAxisRangeSlider());
    }

    // Add captions to tile-footer
    slots['tile-footer'] = [caption()];

    // Note: We don't add y-axis range slider because when step is on y-axis,
    // we're showing all steps as rows (no need to filter/zoom vertically)

    return slots;
  },
};

// Helper functions (internal to this layout)

/**
 * Builds grid using column-by-column logic.
 * This is the main grid building function used for all grid configurations.
 */
function buildColumnByColumnLogic(
  input: TransformInput,
  xAxis: string,
  yAxis: string,
  snapToExistingStep: boolean,
  maxRows: number
): GridData {
  const {runs, stepMeta, config} = input;
  const {mediaKeys, columnCount, selection} = config;

  if (!mediaKeys || mediaKeys.length === 0) {
    return {rows: [], metadata: {layoutType: 'grid', totalCells: 0}};
  }

  const controlledDimension = getControlledDimension(xAxis, yAxis);
  const groupSize = mediaKeys.length;

  // Calculate effective column limit (columnCount divided by group size)
  const effectiveColumnCount = columnCount
    ? Math.floor(columnCount / groupSize)
    : undefined;

  // Extract selection ranges
  const minX = _.get(selection?.xAxis, 0);
  const maxX = _.get(selection?.xAxis, 1);
  const minY = _.get(selection?.yAxis, 0);
  const maxY = _.get(selection?.yAxis, 1);

  log('[buildColumnByColumnLogic] Starting with config:', {
    xAxis,
    yAxis,
    columnCount,
    effectiveColumnCount,
    maxRows,
    selection: {minX, maxX, minY, maxY},
  });

  // Get X-axis values (with filtering and sampling)
  const xAxisResult = getAxisValuesWithOptions(
    xAxis as any,
    runs,
    stepMeta,
    mediaKeys[0],
    snapToExistingStep,
    minX,
    maxX,
    effectiveColumnCount
  );

  // Get Y-axis values (with filtering and sampling)
  const yAxisResult = getAxisValuesWithOptions(
    yAxis as any,
    runs,
    stepMeta,
    mediaKeys[0],
    snapToExistingStep,
    minY,
    maxY,
    maxRows
  );

  // Build rows using helper function
  const rows = buildGridRows(
    xAxis,
    yAxis,
    xAxisResult.values,
    yAxisResult.values,
    controlledDimension,
    input,
    snapToExistingStep,
    effectiveColumnCount
  );

  // Calculate metadata
  const totalCells = rows.reduce((sum, row) => sum + row.cells.length, 0);

  let historySampleRate: number | undefined;
  if (xAxis === 'step' && xAxisResult.values.length > 1) {
    const xValues = xAxisResult.values as number[];
    historySampleRate =
      (xValues[xValues.length - 1] - xValues[0] - 2) / xValues.length;
  } else if (yAxis === 'step' && yAxisResult.values.length > 1) {
    const yValues = yAxisResult.values as number[];
    historySampleRate =
      (yValues[yValues.length - 1] - yValues[0] - 2) / yValues.length;
  }

  log('[buildColumnByColumnLogic] Complete:', {
    rowCount: rows.length,
    totalCells,
    columnsPerRow: rows[0]?.cells.length,
    historySampleRate,
  });

  return {
    rows,
    metadata: {
      layoutType: 'grid',
      totalCells,
      axisData: {
        x:
          xAxis === 'run'
            ? xAxisResult.values
                .map((_, i) => i)
                .filter((v): v is number => v != null)
            : (xAxisResult.values as number[]).filter(
                (v): v is number => v != null
              ),
        y:
          yAxis === 'run'
            ? yAxisResult.values
                .map((_, i) => i)
                .filter((v): v is number => v != null)
            : (yAxisResult.values as number[]).filter(
                (v): v is number => v != null
              ),
      },
      xAxisTicks:
        xAxis !== 'run'
          ? (xAxisResult.values as number[]).filter(
              (v): v is number => v != null
            )
          : undefined,
      allXAxisValues:
        xAxis !== 'run'
          ? (xAxisResult.allValues as number[]).filter(
              (v): v is number => v != null
            )
          : undefined,
      yAxisTicks:
        yAxis !== 'run'
          ? (yAxisResult.values as number[]).filter(
              (v): v is number => v != null
            )
          : undefined,
      allYAxisValues:
        yAxis !== 'run'
          ? (yAxisResult.allValues as number[]).filter(
              (v): v is number => v != null
            )
          : undefined,
      historySampleRate,
    },
  };
}
